import axiosInstance, { axiosInstanceES, axiosInstanceIMG } from "../utils/axio-instance";
import { addToast } from './ui.actions';
import { withRoleDisplayLabels } from '../utils/roleLabels';

const DEBUG = process.env.NODE_ENV === 'development';

export const requestScheduler = {
  queue: [],
  activeRequests: 0,
  maxConcurrent: 6,
  lowPriorityThreshold: 3,
  requestTimeouts: new Map(), // Track timeouts for each request

  enqueue(request, priority = 'low') {
    const requestId = Math.random().toString(36).substr(2, 9);
    const startTime = DEBUG ? Date.now() : 0;

    return new Promise((resolve, reject) => {
      const processRequest = () => {
        if (DEBUG) {
          const queueTime = Date.now() - startTime;
         // console.log(`[Request ${requestId}] Queued for ${queueTime}ms, priority: ${priority}`);
        }

        this.queue.push({ 
          request, 
          resolve, 
          reject, 
          priority,
          id: requestId,
          startTime: Date.now()
        });
        this.processQueue();
      };

      // Add a small delay to batch requests
      const timeoutId = setTimeout(processRequest, 10);
      this.requestTimeouts.set(requestId, timeoutId);
    });
  },

  processQueue() {
    while (this.activeRequests < this.maxConcurrent && this.queue.length > 0) {
      // Find the next high priority request, or low priority if we're under threshold
      let nextRequestIndex = this.queue.findIndex(req => req.priority === 'high');
      
      // If no high priority requests or we're under the low priority threshold, take the next one
      if (nextRequestIndex === -1 || this.activeRequests < this.lowPriorityThreshold) {
        nextRequestIndex = 0;
      }
      
      if (nextRequestIndex === -1) return;
      
      const nextRequest = this.queue.splice(nextRequestIndex, 1)[0];
      if (!nextRequest) continue;

      this.activeRequests++;
      if (DEBUG) {
        const queueTime = Date.now() - nextRequest.startTime;
       // console.log(`[Request ${nextRequest.id}] Processing after ${queueTime}ms in queue`);
      }

      // Add a timeout to prevent hanging requests
      const timeout = setTimeout(() => {
        if (nextRequest.reject) {
          // console.error(`[Request ${nextRequest.id}] Timeout after 30s`);
          nextRequest.reject(new Error('Request timed out after 30s'));
        }
        this.activeRequests = Math.max(0, this.activeRequests - 1);
        this.processQueue();
      }, 30000); // 30 second timeout

      // Execute the request
      nextRequest.request()
        .then(result => {
          clearTimeout(timeout);
          if (nextRequest.resolve) {
            if (DEBUG) {
              const processTime = Date.now() - nextRequest.startTime;
             // console.log(`[Request ${nextRequest.id}] Completed in ${processTime}ms`);
            }
            nextRequest.resolve(result);
          }
        })
        .catch(error => {
          clearTimeout(timeout);
          if (nextRequest.reject) {
           // console.error(`[Request ${nextRequest.id}] Error:`, error);
            nextRequest.reject(error);
          }
        })
        .finally(() => {
          this.activeRequests = Math.max(0, this.activeRequests - 1);
          this.processQueue();
        });
    }
  }
};

export const updateParams = (params) => async (dispatch) => {
  dispatch({ type: 'UPDATE_PARAMS', payload: params });
}

export const getPropertyGroups = (propertyId, priority = 'low') => async (dispatch) => {
  try {
    // Fetch group data by propertyId with priority
    const response = await requestScheduler.enqueue(
      () => axiosInstance.get(`/usrgroup/property/${propertyId}`),
      priority
    );
    
    const groupDataPromises = response.data.map(async (group) => {
      // For user data, we can prioritize even lower
      // const groupResponse = await requestScheduler.enqueue(
      //   () => axiosInstance.get(`/usrgroup/${group.id}/users`),
      //   'lowest'
      // );
      
      return {
        groupId: group.id,
        groupName: group.name,
        // invitees: groupResponse.data.map((invitee) => `${invitee.email.substring(0, 5)}...com`),
      };
    });

    const groupDataArray = await Promise.all(groupDataPromises);
    const groupsDataObject = groupDataArray.reduce((acc, group) => {
      acc[group.groupName] = {
        groupId: group.groupId,
        invitees: group.invitees,
      };
      return acc;
    }, {});
    if(Object.keys(groupsDataObject).length > 0) {
      dispatch({ type: 'PROPERTY_GROUPS', payload: {propertyId, groupsDataObject} });
    }
  } catch (err) {
   console.log(err)
  }
}

export const fetchGroupCounts = () => async (dispatch) => {
  try {
    const response = await requestScheduler.enqueue(
      () => axiosInstance.get(`/usrgroup/all/count`),
      'low' // This is a background task, so we give it low priority
    );
    dispatch({ type: 'GROUP_COUNTS', payload: response.data || {} });
  } catch(error) {
    console.error('Error fetching group counts:', error);
  }
};

// Example of a high-priority API call
export const fetchCriticalData = (data) => async (dispatch) => {
  try {
    const response = await requestScheduler.enqueue(
      () => axiosInstance.post('/critical-endpoint', data),
      'high' // This is a high priority request
    );
    return response.data;
  } catch (error) {
    console.error('Error in fetchCriticalData:', error);
    throw error;
  }
};  
// Add this at the top of search.actions.js
const pendingImageRequests = new Map();

export const fetchImages = (propertyId, priority = 'low') => async (dispatch) => {
  // If we already have a request for this property ID, return its promise
  if (pendingImageRequests.has(propertyId)) {
    return pendingImageRequests.get(propertyId);
  }

  const requestPromise = (async () => {
    try {
      const response = await requestScheduler.enqueue(
        () => axiosInstanceIMG.get(`/images/list/${propertyId}`, {
          timeout: 20000
        }),
        priority
      );

      const images = response.data.images || {};
      const defaultImgs = [];
      
      if (Object.keys(images).length > 0) {
        Object.values(images).forEach(categoryImages => {
          if (Array.isArray(categoryImages)) {
            defaultImgs.push(...categoryImages);
          }
        });
      }

      dispatch({ 
        type: 'PROPERTY_IMAGES', 
        payload: { 
          propertyId, 
          images: defaultImgs 
        } 
      });

      return defaultImgs;
    } catch (error) {
      console.error(`Error fetching images for property ${propertyId}:`, error);
      throw error;
    } finally {
      // Clean up the pending request
      pendingImageRequests.delete(propertyId);
    }
  })();

  // Store the promise in our pending requests map
  pendingImageRequests.set(propertyId, requestPromise);
  return requestPromise;
};
  
export const fetchHomeServices = (buysell) => async (dispatch) => {
  axiosInstance
    .get("/gen/services?side="+buysell+"&is_deleted=false&sort=display_order&limit=300")
    .then((response) => {
      dispatch({ type: 'HOME_SERVICES', payload: {services: response.data.data, side: buysell} });
      dispatch({type: 'LOADING', payload: false});
    })
    .catch((error) => {
      console.log(error);
    });
};

export const fetchAllServices = () => async (dispatch) => {
  axiosInstance
    .get("/gen/all_services")
    .then((response) => {
      dispatch({ type: 'HOME_ALL_SERVICES', payload: {services: response.data?.data} });
      dispatch({type: 'LOADING', payload: false});
    })
    .catch((error) => {
      console.log(error);
    });
};

export const fetchAllRoles = () => async (dispatch) => {
  axiosInstance
    .get("/gen/role-service-categories")
    .then((response) => {
      dispatch({ type: 'HOME_ALL_ROLES', payload: {roles: withRoleDisplayLabels(response.data?.data || [])} });
      dispatch({type: 'LOADING', payload: false});
    })
    .catch((error) => {
      console.log(error);
    });
};


export const fetchHomeQuestions = () => async (dispatch) => {
  axiosInstance
    .get("/gen/questions?sort=service_id&limit=500")
    .then((response) => {
      dispatch({ type: 'HOME_QUESTIONS', payload: response.data.data });
    })
    .catch((error) => {
      console.log(error);
    });
};

export const fetchHomeQuestionsOptions = (questionId) => async (dispatch) => {
  axiosInstance
    .get(`/gen/questionsOptions?field_business_questions_id=${questionId}&sort=display_order`)
    .then((response) => {
      dispatch({ type: 'HOME_QUESTIONS_OPTIONS', payload: {questionsId: questionId, options: response.data.data} });
    })
    .catch((error) => {
      console.log(error);
    });
};

export const saveSearchQuery = async (queryObject) => {
  return await axiosInstance
    .post(`/property/requests`, queryObject);
};

export const saveSearchQueryLocal = (queryObject) => {
  localStorage.setItem("searchQuery", JSON.stringify(queryObject));
}

export const updateSearchQuery = (queryObject) => async (dispatch) => {
  axiosInstance
    .put(`/property/requests/${queryObject.publicId}`, queryObject)
    .then((response) => {
      
      dispatch(addToast({
          title: 'Search Query updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
            position: 'top-right',
        }));
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to update search query. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        }));
    });
};

export const getSearchQueries = (page, size) => async (dispatch) => {
  axiosInstanceES
    .get(`/searchQuery?page=${page}&size=${size}`)
    .then((response) => {
      dispatch({ type: 'GET_SEARCH_QUERY', payload: response.data });
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to get search queries. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        }));
    });
};

export const getFieldData = (fieldIds) => {
  return axiosInstance
    .get(`/cf/fieldData?fieldIds=${fieldIds.join(",")}`)
    .then((response) => {
      return response.data.data;
    })
    .catch((error) => {
        console.log(error);
    });
};

export const fetchCustomFields = (recordType, defaultInd = false, sortName="") => async (dispatch) => {
  console.log("cusome fields loading...")
  try {
    axiosInstance.get("cf/custom-fields/"+recordType+"?defaultInd="+defaultInd+(sortName!==''?"&sort="+sortName:""))
    .then(response => {
      dispatch({ type: 'FETCH_CUSTOM_FIELDS', payload: response.data.data });
    })
    
  } catch (error) {
    console.error("Error fetching custom fields:", error);
  }
}

export const getSearchQueryResponses = (publicId, page, size) => async (dispatch) => {
  axiosInstanceES
    .get(`/searchQuery/responses/${publicId}?page=${page}&size=${size}`)
    .then((response) => {
      const messages = [];
      const responses = [];
      response.data.forEach((item) => {
        if(item.data.message) {
          messages.push(item);
        } else {
          responses.push(item);
        }
      });
      dispatch({ type: 'GET_SEARCH_QUERY_RESPONSES', payload: {messages, responses} });
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to get search query responses. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        }));
    });
}

export const getRequestResponsesCount = (publicId) => async (dispatch) => {
  axiosInstanceES
    .get(`/searchQuery/responses-count/${publicId}`)
    .then((response) => {
      dispatch({ type: 'GET_REQUEST_RESPONSES_COUNT', payload: {publicId, count: response.data.count} });
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to get search query responses count. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
      }));
  });
}

export const updateViewRequestInvite = (token) => async (dispatch) => {
  axiosInstance
    .put(`/cf/requestinvite/${token}`)
    .then((response) => {
      console.log("invite updated with view status");
      //  dispatch(addToast({
      //     title: 'Request updated successfully',
      //     description: 'Request updated successfully.',
      //     status: 'success',
      //     duration: 5000,
      //     isClosable: true,
      //     position: 'top-right',
      // }));
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to update view request invite. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
      }));
  });
}

export const createResponseSession = (requestDetails) => async (dispatch) => {
  axiosInstanceES.post("/save-response", requestDetails)
    .then((response) => {
       localStorage.removeItem("requestResponse")
       dispatch(updateRequestInviteStatus(requestDetails.token));
       dispatch(addToast({
          title: 'Response posted successfully',
          description: 'Response posted successfully.',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        }));
    })
    .catch((error) => {
      dispatch(addToast({
          title: 'Error',
          description: 'Failed to post response. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
      }));
  });
}

const updateRequestInviteStatus = (token) => async (dispatch) => {
  axiosInstance
    .put(`/property/requestinvite/update/${token}/RESPONDED`)
    .then((response) => {
      console.log("status updated")
    })
    .catch((error) => {
      console.log("status update failed")
    });
}

// get unique service and put in redux cache
// /property/services/uniqueservices

export const fetchUniqueServices = () => async (dispatch) => {
  try {
    const response = await axiosInstance.get("/property/services/uniqueservices");
    if (response.data?.result && Array.isArray(response.data.data)) {
      dispatch({ type: 'HOME_ALL_UNIQUE_SERVICES', payload: { services: response.data.data } });
    }
  } catch (error) {
    console.error("Error fetching unique services:", error);
    dispatch(addToast({
      title: 'Error',
      description: 'Failed to fetch unique services. Please try again later.',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    }));
  }
};

export const saveRequestStatus = (status, requestId) => async(dispatch)=>{
    axiosInstanceES.put(`/searchQuery/update-status/${requestId}`, { status }).then(res=>{
      console.log("request updated");
      dispatch(addToast({
          title: 'Success',
          description: 'Request updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        }));
    }).catch(e=>{
      console.error("Error fetching unique services:", e);
      dispatch(addToast({
        title: 'Error',
        description: 'Failed to update Request, Try after sometime...',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      }));
    });
}

export const getParentServices = () => async (dispatch) => {
  try {
    const response = await axiosInstance.get('/staff/services/parent');
    if (response.data?.result && Array.isArray(response.data.data)) {
      dispatch({ type: 'FETCH_PARENT_SERVICES', payload: { parentServices: response.data.data } });
    }
  } catch (error) {
    console.error("Error fetching parent services:", error);
    dispatch(addToast({
      title: 'Error',
      description: 'Failed to fetch parent services. Please try again later.',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    }));
  }
};

